import type { PusherEventPayloadMap } from '@timelyapp/integration-cloud-api-spec';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { getPrivateTicPusherChannelName } from 'redux/selectors/pusher';
import type { ThunkDispatch } from 'types/redux';
import debug from 'utils/debug';
import { fromISO } from 'utils/luxon-helpers';
import { useTicPusherSubscription } from 'utils/pusher';
import { injectAiLogToStatusForDateRange } from '~domains/ai/predictions/redux/predictions.actions';
import {
  handleHourNoteGeneratedPusherEvent,
  handleSuggestionNoteGeneratedPusherEvent,
} from '~domains/ai/predictions/redux/predictions.note.actions';
import { getPredictionQuery } from '~domains/ai/predictions/redux/predictions.selectors';

type Props = {
  /** Ref RTKQ param of same name, skip disables any Pusher side-effects */
  skip?: boolean;
};
const useTicAiPusherManager = ({ skip }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const predictionQuery = useSelector(getPredictionQuery);
  const privateTicChannel = useSelector(getPrivateTicPusherChannelName);

  /**
   * Generic catch-all for AI status updates
   */
  const handleAiStatusUpdate = useCallback(
    (payload: PusherEventPayloadMap['ai_status_update']) => {
      if (skip) {
        debug.template.pusher(`${payload.event} ignored, awaiting AI status..`, { icon: '🚫' });
        return;
      }

      // const isBad = ['training_failed', 'inference_failed'].includes(payload.event);
      // const type = isBad ? 'warning' : 'info';
      // dispatch(
      //   showToast({
      //     type,
      //     title: `AI Pusher event: ${payload.event}`,
      //     message: `${payload.message || 'No message specified'}`,
      //     logLevel: LogLevel.Verbose,
      //   })
      // );

      /**
       * TODO: Client-side injections of the things that don't have trigger/side-effects.
       * PS: remember to filter out "outdated events" (ref API implementation)
       */
      switch (payload.event) {
        // !! DO NOT act on these events, we do so via API success, ref apiSlice.
        case 'training_requested':
        case 'inference_requested': {
          debug.template.pusher(`${payload.event} ignored, alerady handeled on API`, { payload });
          break;
        }
        // Some events don't trigger side-effects, but are useful for e.g UI state.
        // so we can safely inject them locally without AiStatus cache busting.
        case 'model_reset':
        case 'training_failed':
        case 'inference_failed':
        case 'training_running':
          debug.template.pusher(`${payload.event} has NO trigger effect, just set state`, {
            payload,
          });
          dispatch(injectAiLogToStatusForDateRange(payload));
          break;
        // When we have AI trigger side-effects, we must reload the AI status
        // as the "on success" of that execute the AI triggers.
        case 'model_health':
        case 'training_complete':
          debug.template.pusher(`${payload.event} causes re-fetch of AI status`);
          dispatch(ticApi.util.invalidateTags(['AiStatus']));
          break;
        case 'inference_complete': {
          dispatch(ticApi.util.invalidateTags(['AiStatus']));

          // Only refetch if the predictions are within the range
          let shouldRefreshPredictions = true;
          if ('range_from' in payload && 'range_to' in payload) {
            const { startDate, endDate } = predictionQuery;
            const predictionsAreWithinRange =
              fromISO(payload.range_from) >= fromISO(startDate) &&
              fromISO(payload.range_to) <= fromISO(endDate);
            if (!predictionsAreWithinRange) shouldRefreshPredictions = false;
          }

          if (shouldRefreshPredictions) {
            debug.template.ai(`${payload.event} triggers re-fetch of AI status & predictions`);
            dispatch(ticApi.util.invalidateTags(['PredictedIntervals']));
          } else {
            debug.template.ai(`${payload.event} ignored, outside of date range`);
          }
          break;
        }
        default:
          debug.template.ai(`${payload.event} has no side-effects`, { icon: '⌛️' });
          break;
      }
    },
    [dispatch, predictionQuery, skip]
  );
  /**
   * Handle hour-based note generation results
   */
  const handleHourNoteGenerationEvent = useCallback(
    (payload: PusherEventPayloadMap['hour_note_generation']) => {
      dispatch(handleHourNoteGeneratedPusherEvent(payload));
    },
    [dispatch]
  );

  /**
   * Handle suggestion-based note updates
   */
  const handleSuggestionNoteUpdateEvent = useCallback(
    (payload: PusherEventPayloadMap['suggestion_note_update']) => {
      if (skip) {
        debug.template.pusher(`Suggestion note update ignored`, { icon: '🚫' });
        return;
      }
      dispatch(handleSuggestionNoteGeneratedPusherEvent(payload));
    },
    [dispatch, skip]
  );

  /**
   * Register listeners
   */
  useTicPusherSubscription(privateTicChannel, 'ai_status_update', handleAiStatusUpdate);
  useTicPusherSubscription(
    privateTicChannel,
    'hour_note_generation',
    handleHourNoteGenerationEvent
  );
  useTicPusherSubscription(
    privateTicChannel,
    'suggestion_note_update',
    handleSuggestionNoteUpdateEvent
  );
};

export default useTicAiPusherManager;
